import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';

const BulkDeleteTestsModal = ({
    open,
    onClose,
    onTestsDeleted,
    setSuccessAlertMessage,
    setSuccessAlertOpen,
    setErrorAlertMessage,
    setErrorAlertOpen,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [deleteCategory, setDeleteCategory] = useState(false);
    const [confirmStep, setConfirmStep] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [forceDeleteStep, setForceDeleteStep] = useState(false);
    const [studentAnswersCount, setStudentAnswersCount] = useState(0);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/test-categories/with-counts`,
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );
            setCategories(response.data);
        } catch (error) {
            console.error('Error fetching categories:', error);
            setErrorAlertMessage('Failed to fetch categories');
            setErrorAlertOpen(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchCategories();
            setSelectedCategory(null);
            setDeleteCategory(false);
            setConfirmStep(false);
            setForceDeleteStep(false);
            setStudentAnswersCount(0);
        }
    }, [open]);

    const handleCategorySelect = (category) => {
        if (category.testsCount === 0) {
            return; // Can't select categories with no tests
        }
        setSelectedCategory(category);
        setConfirmStep(false);
        setDeleteCategory(false);
    };

    const handleProceedToConfirm = () => {
        setConfirmStep(true);
    };

    const handleBack = () => {
        if (forceDeleteStep) {
            setForceDeleteStep(false);
        } else {
            setConfirmStep(false);
            setSelectedCategory(null);
            setDeleteCategory(false);
        }
    };

    const handleBulkDelete = async (forceDelete = false) => {
        if (!selectedCategory) return;

        setDeleting(true);
        try {
            let url = `${process.env.REACT_APP_URL}/api/v1/test-categories/${encodeURIComponent(selectedCategory.name)}/bulk-delete-tests?deleteCategory=${deleteCategory}`;
            if (forceDelete) {
                url += '&force=true';
            }

            const response = await Axios.delete(url, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            });
            setSuccessAlertMessage(response.data.message);
            setSuccessAlertOpen(true);
            setSelectedCategory(null);
            setConfirmStep(false);
            setDeleteCategory(false);
            setForceDeleteStep(false);
            fetchCategories();
            if (onTestsDeleted) {
                onTestsDeleted();
            }
        } catch (error) {
            console.error('Error bulk deleting tests:', error);

            // Check if this requires force delete (student data exists)
            if (error.response?.data?.requiresForce) {
                setStudentAnswersCount(error.response.data.studentAnswersCount || 0);
                setForceDeleteStep(true);
            } else {
                setErrorAlertMessage(error.response?.data?.error || 'Failed to delete tests');
                setErrorAlertOpen(true);
            }
        } finally {
            setDeleting(false);
        }
    };

    const renderCategoryList = () => (
        <>
            <Typography variant="body1" mb={2}>
                Select a category to delete all its tests:
            </Typography>
            <Box
                sx={{
                    maxHeight: '350px',
                    overflowY: 'auto',
                    border: `1px solid ${colors.grey[700]}`,
                    borderRadius: 1,
                }}
            >
                <List>
                    {categories.length === 0 ? (
                        <ListItem>
                            <ListItemText
                                primary="No categories found"
                                secondary="There are no test categories to manage"
                            />
                        </ListItem>
                    ) : (
                        categories.map((category, index) => (
                            <React.Fragment key={category.id}>
                                {index > 0 && <Divider />}
                                <ListItem
                                    button
                                    selected={selectedCategory?.id === category.id}
                                    onClick={() => handleCategorySelect(category)}
                                    disabled={category.testsCount === 0}
                                    sx={{
                                        cursor: category.testsCount > 0 ? 'pointer' : 'not-allowed',
                                        opacity: category.testsCount === 0 ? 0.5 : 1,
                                        '&.Mui-selected': {
                                            backgroundColor: colors.redAccent[900],
                                        },
                                        '&.Mui-selected:hover': {
                                            backgroundColor: colors.redAccent[800],
                                        },
                                    }}
                                >
                                    <ListItemText
                                        primary={category.name}
                                        secondary={
                                            category.testsCount === 0
                                                ? 'No tests in this category'
                                                : `${category.testsCount} test(s)`
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <Typography
                                            variant="body2"
                                            color={category.testsCount > 0 ? 'error' : 'text.secondary'}
                                        >
                                            {category.testsCount} tests
                                        </Typography>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            </React.Fragment>
                        ))
                    )}
                </List>
            </Box>
        </>
    );

    const renderConfirmation = () => (
        <>
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                <Typography variant="body1" fontWeight="bold">
                    Warning: This action cannot be undone!
                </Typography>
            </Alert>

            <Box
                sx={{
                    p: 2,
                    border: `2px solid ${colors.redAccent[500]}`,
                    borderRadius: 2,
                    backgroundColor: colors.redAccent[900],
                    mb: 2,
                }}
            >
                <Typography variant="h5" fontWeight="bold" color="error" mb={1}>
                    You are about to delete:
                </Typography>
                <Typography variant="body1" mb={1}>
                    <strong>{selectedCategory?.testsCount}</strong> test(s) in category: <strong>"{selectedCategory?.name}"</strong>
                </Typography>

                <Divider sx={{ my: 2 }} />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={deleteCategory}
                            onChange={(e) => setDeleteCategory(e.target.checked)}
                            color="error"
                        />
                    }
                    label={
                        <Typography>
                            Also delete the category "{selectedCategory?.name}" after removing all tests
                        </Typography>
                    }
                />
            </Box>

            <Typography variant="body2" color="text.secondary">
                This will permanently delete all tests and their associated data including:
            </Typography>
            <ul style={{ marginTop: 8, color: colors.grey[400] }}>
                <li>Test files (PDFs)</li>
                <li>All questions and answer options</li>
                <li>All sections</li>
                <li>Course test assignments</li>
                <li>Student answers and scores</li>
            </ul>
        </>
    );

    const renderForceDeleteConfirmation = () => (
        <>
            <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                <Typography variant="body1" fontWeight="bold">
                    CRITICAL WARNING: Student Data Will Be Permanently Deleted!
                </Typography>
            </Alert>

            <Box
                sx={{
                    p: 2,
                    border: `3px solid ${colors.redAccent[400]}`,
                    borderRadius: 2,
                    backgroundColor: colors.redAccent[800],
                    mb: 2,
                }}
            >
                <Typography variant="h5" fontWeight="bold" color="error" mb={2}>
                    ⚠️ Student Data Detected
                </Typography>
                <Typography variant="body1" mb={2}>
                    These tests have <strong style={{ color: '#ff6b6b' }}>{studentAnswersCount} student answer(s)</strong> associated with them.
                </Typography>
                <Typography variant="body1" mb={2}>
                    Proceeding will <strong>permanently delete</strong>:
                </Typography>
                <ul style={{ marginTop: 8, color: '#ff9999' }}>
                    <li>All student test submissions</li>
                    <li>All student answers and scores</li>
                    <li>All grading data</li>
                    <li>Student progress records</li>
                </ul>
                <Divider sx={{ my: 2, borderColor: colors.redAccent[400] }} />
                <Typography variant="body2" color="error" fontWeight="bold">
                    This action is IRREVERSIBLE. Are you absolutely sure?
                </Typography>
            </Box>
        </>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: colors.primary[600],
                    minHeight: '400px',
                },
            }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <DeleteIcon color="error" />
                    <Typography variant="h4" fontWeight="bold">
                        Bulk Delete Tests
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : forceDeleteStep ? (
                    renderForceDeleteConfirmation()
                ) : confirmStep ? (
                    renderConfirmation()
                ) : (
                    renderCategoryList()
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                {forceDeleteStep ? (
                    <>
                        <Button onClick={handleBack} color="inherit" disabled={deleting}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => handleBulkDelete(true)}
                            disabled={deleting}
                            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                            sx={{
                                backgroundColor: '#b71c1c',
                                '&:hover': { backgroundColor: '#7f0000' }
                            }}
                        >
                            {deleting ? 'Deleting...' : `Force Delete (${studentAnswersCount} student answers)`}
                        </Button>
                    </>
                ) : confirmStep ? (
                    <>
                        <Button onClick={handleBack} color="inherit" disabled={deleting}>
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => handleBulkDelete(false)}
                            disabled={deleting}
                            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                        >
                            {deleting ? 'Deleting...' : `Delete ${selectedCategory?.testsCount} Tests`}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={onClose} color="inherit">
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleProceedToConfirm}
                            disabled={!selectedCategory}
                        >
                            Proceed
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default BulkDeleteTestsModal;
